import api from "@/api/api";
import documentService from "@/services/document.service";
import type { Support } from "@/types/support";
import { ref, watch } from "@vue/runtime-core";
import { defineStore } from "pinia";
import { useCurrentBrand, useCurrentUser } from "@/utils/storage";

interface SupportWithBrandId extends Support {
  brand_id?: number;
}

export const useDocumentStore = defineStore("document", () => {
  const documents = ref<Support[]>([]);
  const filteredDocuments = ref<Support[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const currentPage = ref(1);
  const totalPages = ref(1);
  const totalDocuments = ref(0);

  // Utiliser les hooks réactifs
  const { brand: currentBrand } = useCurrentBrand();
  const { userId } = useCurrentUser();

  // Watcher pour recharger les documents quand la marque ou l'utilisateur change
  watch([currentBrand, userId], ([newBrand], [oldBrand]) => {
    if (newBrand !== oldBrand && userId.value) {
      console.log('🔄 Rechargement des documents pour la nouvelle marque:', newBrand);
      fetchDocuments(1);
    }
  });

  const fetchDocuments = async (page?: number) => {
    try {
      loading.value = true;
      error.value = null;
      if (page) {
        currentPage.value = page;
      }

      // Utiliser les valeurs réactives au lieu du localStorage
      const userIdValue = userId.value;
      const brandValue = currentBrand.value;

      if (!userIdValue) {
        console.warn('Pas d\'utilisateur connecté, impossible de charger les documents');
        loading.value = false;
        return;
      }

      console.log("userData:", { id: userIdValue });
      console.log("brand:", brandValue);

      const response = await api.get(`/supports/my`, {
        params: {
          page: currentPage.value,
          owner_id: userIdValue,
          brand: brandValue,
          order: "created_at",
          direction: "desc",
        },
      });
      console.log("API response:", response.data);

      if (!response.data.data) {
        console.error("Pas de supports dans la réponse:", response.data);
        return;
      }

      // Le backend gère le tri, on utilise les données directement
      documents.value = response.data.data;
      filteredDocuments.value = response.data.data;
      totalPages.value = response.data.last_page || 1;
      totalDocuments.value = response.data.total || response.data.data.length;
      currentPage.value = response.data.current_page || 1;

      console.log("Documents chargés:", documents.value);
    } catch (err: any) {
      error.value = err.message || "Erreur lors du chargement des documents";
      console.error("Erreur lors du chargement des documents:", err);
    } finally {
      loading.value = false;
    }
  };

  const setFilteredDocuments = (docs: Support[]) => {
    filteredDocuments.value = docs;
  };

  const createDocument = async (document: Partial<Support>) => {
    try {
      loading.value = true;
      error.value = null;
      const newDocument = await documentService.createDocument(document);
      // @ts-ignore
      documents.value.push(newDocument);
      return newDocument;
    } catch (err: any) {
      error.value = err.message || "Erreur lors de la création du document";
      console.error("Erreur lors de la création du document:", err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const updateDocument = async (id: string, document: Partial<Support>) => {
    try {
      loading.value = true;
      error.value = null;
      const updatedDocument = await documentService.updateDocument(
        id,
        document
      );
      const index = documents.value.findIndex((d) => d.id === Number(id));
      if (index !== -1) {
        // @ts-ignore
        documents.value[index] = updatedDocument;
      }
      return updatedDocument;
    } catch (err: any) {
      error.value = err.message || "Erreur lors de la mise à jour du document";
      console.error("Erreur lors de la mise à jour du document:", err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const deleteDocument = async (id: string) => {
    try {
      loading.value = true;
      error.value = null;
      await api.delete(`/supports/my/${id}`);
      documents.value = documents.value.filter((d) => d.id !== Number(id));
    } catch (err: any) {
      error.value = err.message || "Erreur lors de la suppression du document";
      console.error("Erreur lors de la suppression du document:", err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const uploadDocument = async (file: File) => {
    try {
      loading.value = true;
      error.value = null;
      const newDocument = await documentService.uploadDocument(file);
      // @ts-ignore
      documents.value.push(newDocument);
      return newDocument;
    } catch (err: any) {
      error.value = err.message || "Erreur lors du téléchargement du document";
      console.error("Erreur lors du téléchargement du document:", err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const duplicateDocument = async (
    document: SupportWithBrandId,
    brand: string,
    name: string
  ) => {
    try {
      loading.value = true;
      error.value = null;

      // Déterminer le support_group_id en fonction de la marque
      let supportGroupId = 1; // HV par défaut
      if (brand === "valtra" || brand === "Valtra") {
        supportGroupId = 2;
      } else if (brand === "fendt" || brand === "Fendt") {
        supportGroupId = 3;
      }

      const response = await api.post(`/supports/duplicate/${document.id}`, {
        brand_id: supportGroupId,
        name,
        support_group_id: supportGroupId,
      });

      // Vérifier si la marque a changé
      const currentBrand = localStorage.getItem("brand") || "hv";
      const newBrand = brand.toLowerCase();
      const workspaceChanged = currentBrand !== newBrand;

      // Mettre à jour le localStorage avec la nouvelle marque
      localStorage.setItem("brand", newBrand);

      // Si la marque a changé, recharger la page pour mettre à jour le workspace
      if (workspaceChanged) {
        const brandNames = {
          hv: "Horizon Vert",
          valtra: "Valtra",
          fendt: "Fendt",
        };
        const brandName =
          brandNames[newBrand as keyof typeof brandNames] || newBrand;

        // Enregistrer le timestamp du changement
        localStorage.setItem("last_brand_change_time", Date.now().toString());

        // Retourner les données avec l'information sur le changement de workspace
        // On ne fait PAS fetchDocuments car on va recharger la page
        return {
          ...response.data,
          workspaceChanged: true,
          newBrand,
          previousBrand: currentBrand,
          reloadRequired: true,
        };
      }

      await fetchDocuments(currentPage.value);

      // Retourner les données avec l'information sur le changement de workspace
      return {
        ...response.data,
        workspaceChanged: false,
        newBrand,
        previousBrand: currentBrand,
        reloadRequired: false,
      };
    } catch (err: any) {
      error.value = err.message || "Erreur lors de la duplication du document";
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const duplicateMultipleDocuments = async (
    documents: SupportWithBrandId[],
    brand: string
  ) => {
    try {
      loading.value = true;
      error.value = null;

      const promises = documents.map((doc) =>
        duplicateDocument(doc, brand, `${doc.name}`)
      );
      await Promise.all(promises);
      await fetchDocuments(currentPage.value);
    } catch (err: any) {
      error.value =
        err.message || "Erreur lors de la duplication des documents";
      throw err;
    } finally {
      loading.value = false;
    }
  };

  return {
    documents,
    filteredDocuments,
    loading,
    error,
    currentPage,
    totalPages,
    totalDocuments,
    fetchDocuments,
    setFilteredDocuments,
    createDocument,
    updateDocument,
    deleteDocument,
    uploadDocument,
    duplicateDocument,
    duplicateMultipleDocuments,
  };
});
